import { Keys } from '../../config/keys';
import { EStreamTradeDTO } from '../../types/estream/eStreamTrade';
import {
  InstrumentStoreItem,
  InstrumentStorePrice,
  InstrumentStorePriceType,
} from '../../types/estream/instrumentStoreItem';
import { InstrumentCategoryEnum, ListedInstrument } from '../../types/instrument/listedInstrument';
import {
  EStreamMapperConsts,
  EStreamPriceMapperStateContext,
  EStreamPriceMapperStateMachine,
  StateMachine_Market1,
} from './eStreamPriceMapperStateMachine';

const getFallbackPrice = (
  previousValue: InstrumentStoreItem,
  listedInstrument: ListedInstrument | undefined,
): InstrumentStorePrice | undefined => {
  if (previousValue.price) {
    return previousValue.price;
  }
  if (listedInstrument?.lastClosePrice === undefined) {
    return undefined;
  }
  return {
    value: listedInstrument.lastClosePrice,
    type: InstrumentStorePriceType.FromInstrumentService,
  };
};

export const mapPrice = ({
  item,
  previousValue,
  hasRealtimeAccess,
  isPolled,
  listedInstrument,
  formatDate,
  nonTradingDays,
  consts,
}: {
  item: EStreamTradeDTO;
  previousValue: InstrumentStoreItem;
  hasRealtimeAccess: boolean;
  isPolled?: boolean;
  listedInstrument: ListedInstrument | undefined;
  formatDate: (date: Date) => string;
  nonTradingDays: Record<string, Array<string>>;
  consts: EStreamMapperConsts;
}): InstrumentStorePrice | undefined => {
  const mic =
    listedInstrument?.mic === Keys.Markets.Mic.Unknown ? listedInstrument?.mainMarketMICCode : listedInstrument?.mic;

  // handle missing data
  if ((!mic && listedInstrument?.instrumentCategory !== InstrumentCategoryEnum.Currency) || !item.data) {
    if (previousValue.price) {
      return previousValue.price;
    }
    // fallback to listedInstrument last close price
    if (listedInstrument?.lastClosePrice !== undefined) {
      return {
        type: InstrumentStorePriceType.FromInstrumentService,
        value: listedInstrument.lastClosePrice,
      };
    }

    // no price available
    return getFallbackPrice(previousValue, listedInstrument);
  }

  const context: EStreamPriceMapperStateContext = {
    market: mic ?? '',
    data: item.data,
    previousValue,
    hasRealtimeAccess,
    isPolled: isPolled ?? false,
    listedInstrument,
    formatDate,
    nonTradingDays,
    consts,
  };

  let res: EStreamPriceMapperStateMachine | InstrumentStorePrice | undefined = StateMachine_Market1;
  while (true) {
    if (res === undefined) {
      return getFallbackPrice(previousValue, listedInstrument);
    } else if ((res as InstrumentStorePrice).type) {
      return res as InstrumentStorePrice;
    } else {
      res = (res as EStreamPriceMapperStateMachine).next(context);
    }
  }
};
